import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {useState} from "react";
import {useTranslation} from "react-i18next";

import type {ValueOrderType} from "@reva/shared/value-order";

import {useRevaDispatch} from "../redux/revaHooks";
import type {ApiQueryError} from "../redux/types";
import {detachValueOrder} from "../redux/valueOrdersThunk";
import {buildApiQueryError, generateDefaultQueryError} from "../util/errorUtil";
import I18nErrorMessage from "./I18nErrorMessage";

interface DetachInspectionOrderDialogProps {
    /** state if the dialog should be visible; without a valueOrderGuid the dialog stays hidden */
    visible: boolean;
    /** order type to detach */
    orderType?: ValueOrderType;
    /** guid of the value order to detach */
    valueOrderGuid?: string | null;
    /** external order number of the value order, for displaying only */
    externalOrderNumber?: string | null;
    /** callback to execute when the dialog gets closed */
    onClose: () => void;
}

/**
 * Dialog to confirm detaching an Inspection Value Order from the REVA valuation.
 */
export function DetachInspectionOrderDialog({
    visible,
    orderType = "Inspection",
    valueOrderGuid,
    externalOrderNumber,
    onClose,
}: DetachInspectionOrderDialogProps) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const [inProgress, setInProgress] = useState(false);
    const [detachError, setDetachError] = useState<ApiQueryError | null>(null);

    const dialogVisible = visible && !!valueOrderGuid;

    const handleCancel = () => {
        if (inProgress) return;

        setDetachError(null);
        onClose();
    };

    const handleDetach = async () => {
        if (inProgress || !valueOrderGuid) return;

        setInProgress(true);
        setDetachError(null);

        try {
            await dispatch(detachValueOrder(valueOrderGuid, orderType));
        } catch (e) {
            setInProgress(false);
            setDetachError(buildApiQueryError(generateDefaultQueryError(e as Error)));
            return;
        }

        setInProgress(false);
        onClose();
    };

    const footer = (
        <div>
            <Button
                className="p-button-text"
                label={t("general.cancel")}
                icon="pi pi-times"
                disabled={inProgress}
                onClick={handleCancel}
            />
            <Button
                className="ml-2"
                label={t("detachOrderDialog.detach")}
                icon="pi pi-check"
                autoFocus
                disabled={inProgress}
                loading={inProgress}
                onClick={() => void handleDetach()}
            />
        </div>
    );

    return (
        <Dialog
            visible={dialogVisible}
            onHide={handleCancel}
            footer={footer}
            header={t("detachOrderDialog.title")}
            showCloseIcon={false}
            style={{maxWidth: "50rem"}}
        >
            <div>{t("detachOrderDialog.text", {externalOrderNumber})}</div>
            <div className="mt-4">{t("detachOrderDialog.text2")}</div>
            <div className="mt-4">{t("detachOrderDialog.text3")}</div>
            <I18nErrorMessage titleI18nKey="detachOrderDialog.error" errorObject={detachError} className="mt-8" />
        </Dialog>
    );
}
